/**
 * Composable per Cross Company Target Workers autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { CrossCompanyTargetWorkersOptions, AutocompleteResult } from '@/lib/autocompletes';
import { getCrossCompanyTargetWorkersAutocomplete } from '@/lib/autocompletes/crossCompanyTargetWorkers';
// TODO: Usare CrossCompanyTargetWorkerAutocompleteResult quando sarà creato

export function useCrossCompanyTargetWorkersAutocomplete(
	options: BaseAutocompleteComposableOptions = {},
) {
	const targetWorkers = ref<AutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchCrossCompanyTargetWorkers(
		options: CrossCompanyTargetWorkersOptions,
	): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getCrossCompanyTargetWorkersAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					targetWorkers.value = result;
				}
			} catch (err) {
				console.error(
					'❌ ERROR useCrossCompanyTargetWorkersAutocomplete.searchCrossCompanyTargetWorkers:',
					err,
				);
				error(
					'Errore durante il caricamento dei lavoratori cross-company! Ricaricare la pagina e riprovare.',
				);
				if (token.isLatest()) {
					targetWorkers.value = [];
				}
			}
		}, 'searchCrossCompanyTargetWorkers');
	}

	function clearTargetWorkers(): void {
		targetWorkers.value = [];
	}

	return {
		targetWorkers: readonly(targetWorkers),
		searchCrossCompanyTargetWorkers,
		clearTargetWorkers,
	};
}
